import { useId, useState, type JSX } from 'react';
import { Box, Button, Group, Stack, Switch, Text, Title, Tooltip, rem } from '@mantine/core';
import { IconTrash, IconAlertTriangle } from '@tabler/icons-react';
import { type UploadOptions } from '@/db';
import { clearDone, enqueueFile } from '@/lib/upload/queue';
import { DropZone } from '@/components/upload/DropZone';
import { UploadQueueList } from '@/components/upload/UploadQueueList';
import { useUploadModal } from '@/components/upload/upload-modal-context';
import { useUploadQueue, useUploadStats } from '@/lib/upload/use-upload-stats';
import { LangSwitcher } from '@/components/LangSwitcher';
import { type LangCode } from '@/lib/supported-langs';

const BASE_OPTS: UploadOptions = {
  autoCategorize: false,
  generateSeo: false,
  langCode: 'pl',
  auto_ai_pipeline: true,
  lang_code: 'pl',
  override: false,
};

// Warm-palette tokens shared across the options panel.
const LINE = '#ece4d8';
const ROW_INK = '#3a332a';
const ROW_MUTED = '#6f6656';
const GREEN = '#3f7d5b';
const RUST = '#b03a2e';

/**
 * One row of the pipeline-options panel: a labelled Switch with an always-visible
 * one-line description. `danger` marks a destructive toggle (rust accent + alert
 * glyph + a faint rust wash while active).
 */
function PipelineToggleRow({
  title,
  description,
  checked,
  onChange,
  color,
  danger = false,
  withDivider = false,
}: {
  title: string;
  description: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  color: string;
  danger?: boolean;
  withDivider?: boolean;
}): JSX.Element {
  const titleId = useId();
  const descId = useId();
  return (
    <Box
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '11px 14px',
        borderTop: withDivider ? `1px solid ${LINE}` : undefined,
        background: danger && checked ? '#fbeee9' : undefined,
        transition: 'background 140ms ease',
      }}
    >
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Group gap={5} wrap="nowrap" align="center">
          {danger && (
            <IconAlertTriangle size={13} color={RUST} style={{ flexShrink: 0 }} aria-hidden="true" />
          )}
          <Text
            id={titleId}
            style={{
              fontSize: 13,
              fontWeight: 600,
              // Rust only when the destructive toggle is active (red = "on"); a small
              // persistent rust glyph still marks it as the sensitive control when off.
              color: danger && checked ? RUST : ROW_INK,
              lineHeight: 1.25,
            }}
          >
            {title}
          </Text>
        </Group>
        <Text id={descId} style={{ fontSize: 11.5, color: ROW_MUTED, lineHeight: 1.3, marginTop: 1 }}>
          {description}
        </Text>
      </Box>
      <Switch
        checked={checked}
        onChange={(e) => onChange(e.currentTarget.checked)}
        color={color}
        size="sm"
        aria-labelledby={titleId}
        aria-describedby={descId}
        style={{ flexShrink: 0 }}
      />
    </Box>
  );
}

function StatPill({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}): JSX.Element {
  return (
    <Group gap={6} wrap="nowrap" align="baseline">
      <Text size="xs" c="#807563">
        {label}
      </Text>
      <Text
        style={{
          fontFamily: "'Fraunces', Georgia, serif",
          fontSize: 16,
          fontWeight: 600,
          color,
          lineHeight: 1,
        }}
      >
        {value}
      </Text>
    </Group>
  );
}

export interface MassUploadPanelProps {
  /** Hide the top-level "Mass upload" title (modal already has a header). */
  hideTitle?: boolean;
}

export function MassUploadPanel({ hideTitle = false }: MassUploadPanelProps): JSX.Element {
  const [autoAi, setAutoAi] = useState(true);
  const [langCode, setLangCode] = useState<LangCode>('pl');
  const [override, setOverride] = useState(false);

  const { close } = useUploadModal();
  const queue = useUploadQueue();
  const stats = useUploadStats();

  const handleDrop = async (files: File[]): Promise<void> => {
    const opts: UploadOptions = {
      ...BASE_OPTS,
      auto_ai_pipeline: autoAi,
      lang_code: langCode,
      langCode,
      override,
    };
    for (const file of files) {
      await enqueueFile(file, opts);
    }
  };

  const handleClearDone = async (): Promise<void> => {
    await clearDone();
  };

  return (
    <Stack gap={20} style={{ paddingTop: hideTitle ? 12 : 0 }}>
      {!hideTitle && (
        <Title
          order={1}
          style={{
            fontFamily: "'Fraunces', Georgia, serif",
            fontSize: 32,
            fontWeight: 600,
            letterSpacing: -0.5,
            margin: 0,
            color: '#1f1a14',
          }}
        >
          Mass upload
        </Title>
      )}

      <Stack gap={20}>
        {/* ─────────── Uploader card ─────────── */}
        <Box
          style={{
            background: '#ffffff',
            border: `1px solid ${LINE}`,
            borderRadius: 12,
            padding: 16,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 16,
            alignItems: 'stretch',
          }}
        >
          {/* Left: pipeline options — a compact settings panel paired with the dropzone */}
          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              border: `1px solid ${LINE}`,
              borderRadius: 12,
              background: '#fbf7f2',
              overflow: 'hidden',
            }}
          >
            <PipelineToggleRow
              title="Auto-run AI pipeline"
              description="Categorize and write SEO right after upload."
              checked={autoAi}
              onChange={setAutoAi}
              color={GREEN}
            />
            <PipelineToggleRow
              title="Override existing"
              description="Replaces the image on records that already have one."
              checked={override}
              onChange={setOverride}
              color={RUST}
              danger
              withDivider
            />
            <Box style={{ padding: '11px 14px', borderTop: `1px solid ${LINE}` }}>
              <Text style={{ fontSize: 13, fontWeight: 600, color: ROW_INK, lineHeight: 1.25 }}>
                SEO language
              </Text>
              <Text
                style={{
                  fontSize: 11.5,
                  color: ROW_MUTED,
                  lineHeight: 1.3,
                  marginTop: 1,
                  marginBottom: 8,
                }}
              >
                Language the AI writes the SEO description in.
              </Text>
              <LangSwitcher
                value={langCode}
                onChange={setLangCode}
                ariaLabel="SEO description language"
              />
            </Box>
          </Box>

          {/* Right: DropZone (rules live inside it, next to the format line) */}
          <Box style={{ display: 'flex' }}>
            <Box style={{ flex: 1, display: 'flex' }}>
              <DropZone
                onDrop={handleDrop}
                rules={
                  <Group gap={7} justify="center" wrap="nowrap">
                    <Box
                      component="code"
                      style={{
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                        fontSize: 11,
                        color: '#6a5f4d',
                        background: '#f4ede3',
                        padding: '1px 6px',
                        borderRadius: 4,
                      }}
                    >
                      {'{id}.jpg'}
                    </Box>
                    <Text style={{ fontSize: 11.5, color: ROW_MUTED }}>
                      · fits inside 1800×1800
                    </Text>
                  </Group>
                }
              />
            </Box>
          </Box>
        </Box>

        {/* ─────────── Queue card ─────────── */}
        <Box
          style={{
            background: '#ffffff',
            border: '1px solid #ece4d8',
            borderRadius: 12,
            display: 'flex',
            flexDirection: 'column',
            minHeight: rem(420),
          }}
        >
          {/* Header = a single tidy counter row + icon Clear (no "Queue" word,
              no dup Total/queue counter). Absent entirely when the queue is empty. */}
          {stats.total > 0 && (
            <Group
              justify="space-between"
              align="center"
              wrap="nowrap"
              px={14}
              py={10}
              style={{ borderBottom: '1px solid #ece4d8' }}
            >
              <Group gap={14} wrap="wrap">
                <StatPill label="Total" value={stats.total} color="#1f1a14" />
                <StatPill label="Ready" value={stats.ready} color="#3f7d5b" />
                <StatPill label="Working" value={stats.working} color="#4a6b8a" />
                <StatPill label="Errors" value={stats.errors} color="#b03a2e" />
              </Group>
              {stats.ready > 0 && (
                <Tooltip label={`Clear ${stats.ready} completed`} position="top" withArrow>
                  <Button
                    variant="subtle"
                    size="compact-sm"
                    leftSection={<IconTrash size={14} />}
                    onClick={() => void handleClearDone()}
                    aria-label={`Clear ${stats.ready} completed`}
                    style={{ borderRadius: 6, border: '1px solid #ece4d8', color: '#3f7d5b' }}
                  >
                    Clear
                  </Button>
                </Tooltip>
              )}
            </Group>
          )}
          <UploadQueueList items={queue} onNavigate={close} />
        </Box>
      </Stack>
    </Stack>
  );
}
